import { router } from "expo-router";
import { Pressable, ScrollView, View, useWindowDimensions } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Button } from "~/components/ui/Button";
import { CounterCard } from "~/features/counters/CounterCard";
import { Icon } from "~/components/ui/Icon";
import { RuleLine } from "~/components/ui/RuleLine";
import { BodyText, CaptionText, Heading1 } from "~/components/ui/Text";
import { useAuth } from "~/hooks/useAuth";
import { useCounters } from "~/state/counters";
import { layout, spacing, useTheme } from "~/styles/theme";

const iconButton = {
  width: 44,
  height: 44,
  alignItems: "center",
  justifyContent: "center",
} as const;

export default function HomeScreen() {
  const { colors } = useTheme();
  const { counters, loading } = useCounters();
  const { user, configured } = useAuth();
  const { width } = useWindowDimensions();
  const columns = width >= 720 ? 3 : 2;
  const fillers = (columns - (counters.length % columns)) % columns;

  return (
    <SafeAreaView
      style={{ flex: 1, backgroundColor: colors.background }}
      edges={["top", "left", "right"]}
    >
      <View
        style={{
          flex: 1,
          width: "100%",
          maxWidth: layout.pageMaxWidth,
          alignSelf: "center",
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            paddingLeft: spacing.lg,
            paddingRight: spacing.xs,
            paddingTop: spacing.sm,
            paddingBottom: spacing.sm,
          }}
        >
          <Heading1 style={{ letterSpacing: -1 }}>DURANCE</Heading1>
          <View style={{ flexDirection: "row" }}>
            <Pressable
              onPress={() => router.push("/account")}
              accessibilityRole="button"
              style={iconButton}
              accessibilityLabel="Account"
            >
              <Icon name={user ? "user-check" : "user"} />
            </Pressable>
            <Pressable
              onPress={() => router.push("/counter/new")}
              accessibilityRole="button"
              style={iconButton}
              accessibilityLabel="Add counter"
            >
              <Icon name="plus" />
            </Pressable>
          </View>
        </View>
        <View style={{ marginHorizontal: spacing.lg }}>
          <RuleLine strong />
        </View>

        {!user && configured ? (
          <View
            style={{
              paddingHorizontal: spacing.lg,
              paddingVertical: spacing.sm,
            }}
          >
            <CaptionText color={colors.muted}>
              Tracking locally on this device. Sign in to sync across devices.
            </CaptionText>
          </View>
        ) : null}

        {loading ? (
          <View
            style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
          >
            <CaptionText color={colors.muted}>Loading…</CaptionText>
          </View>
        ) : counters.length === 0 ? (
          <View
            style={{
              flex: 1,
              alignItems: "center",
              justifyContent: "center",
              paddingHorizontal: spacing.xxl,
              gap: spacing.sm,
            }}
          >
            <BodyText weight="semibold" align="center">
              Nothing tracked yet
            </BodyText>
            <CaptionText color={colors.muted} align="center">
              Add a date to count days since or count down to.
            </CaptionText>
            <View style={{ marginTop: spacing.md }}>
              <Button
                label="Add counter"
                onPress={() => router.push("/counter/new")}
              />
            </View>
          </View>
        ) : (
          <ScrollView
            contentContainerStyle={{
              flexDirection: "row",
              flexWrap: "wrap",
              borderTopWidth: 1,
              borderLeftWidth: 1,
              borderColor: colors.rule,
              margin: spacing.lg,
              alignSelf: "stretch",
            }}
          >
            {counters.map((counter) => (
              <CounterCard
                key={counter.id}
                counter={counter}
                columns={columns}
                onPress={() => router.push(`/counter/${counter.id}`)}
              />
            ))}
            {Array.from({ length: fillers }, (_, index) => (
              <View
                key={`filler-${index}`}
                style={{
                  width: `${100 / columns}%`,
                  minHeight: 148,
                  borderRightWidth: 1,
                  borderBottomWidth: 1,
                  borderColor: colors.rule,
                }}
              />
            ))}
          </ScrollView>
        )}
      </View>
    </SafeAreaView>
  );
}
